import { useDisclosure } from '@mantine/hooks'
import { IconBookmark, IconClockHour4, IconMovie } from '@tabler/icons-react'
import { type ReactNode, StrictMode } from 'react'
import { AppShell, Box, Burger, Button, Container, createTheme, Group, MantineProvider, Menu, MenuTarget, NavLink, Stack } from '@mantine/core'
import { createRootRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import logo from './images/logo.png'
import './index.css'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './queryClient.ts'
import { Notifications } from '@mantine/notifications'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { savedMoviesRoute } from './pages/saved_movies.tsx'
import { upcomingRoute } from './pages/upcoming.tsx'

const theme = createTheme({
  colors: {
    primary: [
      '#f4f0ff',
      '#e4def4',
      '#c5bbe1',
      '#a595cf',
      '#8975bf',
      '#7861b6',
      '#694fad',
      '#5f479d',
      '#543e8d',
      '#48357e',
    ],
  },
  primaryColor: 'primary',
  autoContrast: true,
  fontFamily: 'Roboto, Open Sans, sans-serif',
})

export const rootRoute = createRootRoute({ component: Root })

function Root() {

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <TanStackRouterDevtools/>
        <MantineProvider theme={theme} defaultColorScheme="dark">
          <Notifications/>
          <AppShell
            padding="md"
            header={{
              height: {
                base: 60,
                md: 80,
              }
            }}
            footer={{ height: 60 }}
          >
            <Header/>

            <AppShell.Main className="main">
              <Container p={{ base: '0', sm: 'md' }}>
                <Outlet/>
              </Container>
            </AppShell.Main>

            <Box bg="rgba(0, 0, 0, 0.05)" mt="xl">
              <Container>
                <Group justify="center" p="md" c="gray">
                  © 2026 Caʒ
                </Group>
              </Container>
            </Box>

            <MobileAppFooter/>
          </AppShell>
        </MantineProvider>
      </QueryClientProvider>
    </StrictMode>
  )
}

function Header() {
  const [opened, { toggle }] = useDisclosure()

  return (
    <AppShell.Header className="header">
      <Container h="100%">
        <Group justify="space-between" align="center" h="100%">
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit', height: '100%' }}>
            <Group align="center" gap="sm" h="100%">
              <img src={logo} alt="logo" style={{ height: '100%', marginTop: '1px' }}/>
              Mewvie Watchcat
            </Group>
          </Link>

          <Menu position="bottom-end" opened={opened} onClose={toggle}>
            <Menu.Target>
              <Burger
                opened={opened}
                onClick={toggle}
                hiddenFrom="sm"
                size="sm"
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item>
                <HeaderLink to={savedMoviesRoute.to}>Saved</HeaderLink>
              </Menu.Item>
              <Menu.Item>
                <HeaderLink to={upcomingRoute.to}>Upcoming</HeaderLink>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          <Group visibleFrom="sm" h="100%" align="center" wrap="nowrap" gap="0">
            <HeaderLink to={savedMoviesRoute.to}>Saved</HeaderLink>
            <HeaderLink to={upcomingRoute.to}>Upcoming</HeaderLink>
          </Group>
        </Group>
      </Container>
    </AppShell.Header>
  )
}

function HeaderLink({ children, to }: { children: ReactNode, to: string }) {
  return (
    <NavLink
      component={Link}
      h="100%"
      to={to}
      variant="subtle"
      label={children}
      active={false}
      style={{ color: 'inherit' }}
      styles={{
        body: { display: 'flex', alignItems: 'center' },
      }}
    />
  )
}

function MobileAppFooter() {
  return (
    <AppShell.Footer className="mobile-footer">
      <Button.Group style={{ height: '100%', boxShadow: 'rgba(0, 0, 0, 0.3) 0px -5px 10px 10px' }}>
        <MobileFooterButton label="In cinemas" path="/" icon={<IconMovie/>}/>
        <MobileFooterButton label="Upcoming" path="/upcoming" icon={<IconClockHour4/>}/>
        <MobileFooterButton label="Saved" path="/saved" icon={<IconBookmark/>}/>
      </Button.Group>
    </AppShell.Footer>
  )
}

function MobileFooterButton({label, path, icon}: { label: string, path: string, icon: ReactNode }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()

  return (
    <Button radius="0" w="100%" variant="default" bg="dark.5" c={pathname === path ? 'primary.3' : undefined} onClick={() => navigate({ to: path })}>
      <Stack align="center" justify="center" gap="4px">
        {icon}
        {label}
      </Stack>
    </Button>
  )
}